import React, { useEffect, useState } from 'react';
import Search from '../../../components/search/search';
import { DynamicTableFilter, TableParams } from '../../../types/common';
import Filter from '../../../components/filter';
import Heading from '../../../components/typography';
import CustomDatePicker from '../../../components/date-picker/customDatePicker';
import useDynamicTable from '../hooks/useDynamicTable';
import { isEmpty, isNull } from 'lodash';
import moment from 'moment';
import { DATE_FORMAT } from '@packages/shared/constants';
import FormSelect from '../../../components/dropdown/formSelect';
import { ResetFilterWrapper } from '../../../constants/style/common.styled';
import InitialValuesForm from '../../../constants/initialValues';
import { useSelector } from 'react-redux';
import { selectToken } from '../../../pages/auth/authSlice';
import { axiosInstance } from '../../../services/apiInterceptor';
import { useQuery } from 'react-query';
import { SkeletonLine } from '../../../components/skeleton';
import clsx from 'clsx';
import { SelectedLabelContainer } from '../index.styled';
import { getFormattedKeyValuePairs } from '../utils/utils';
import { COLORS } from '../../../constants/style/color';
import Button from '../../../components/button';

type ToolbarProps = {
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  tableInfo: TableParams;
  setTableInfo: (value: TableParams) => void;
  showFilter?: boolean;
  filters?: DynamicTableFilter[];
  filterControls?: Record<string, Record<string, any>>;
  components?: Record<string, () => JSX.Element>;
  filterColumn?: number;
  showSearch?: boolean;
  showSelectedFilter?: boolean;
  totalCounts: number;
};

const clearIfExist = (data: Record<string, any>, clears?: string[]) => {
  clears?.forEach((item) => {
    delete data[item];
  });
};

function Toolbar(data: ToolbarProps) {
  const {
    searchQuery,
    setSearchQuery,
    tableInfo,
    setTableInfo,
    showFilter,
    filterControls,
    components,
    filterColumn = 2,
    showSearch = true,
    showSelectedFilter,
    totalCounts,
  } = data;
  const [open, setOpen] = React.useState(false);
  const [filterValues, setFilterValues] = React.useState<TableParams>(tableInfo);
  const { labeledFilter, setLabeledFilter } = useDynamicTable();
  const [selectedLabeledFilter, setSelectedLabeledFilter] = useState<any[]>();

  useEffect(() => {
    setFilterValues(tableInfo);
  }, [tableInfo]);

  const handleFilterFieldChange = (name: string, value: any) => {
    setFilterValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  return (
    <>
      <div className='grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4 mb-8'>
        <div className='col-span-2 md:col-span-2 lg:col-span-2 grid grid-cols-4 gap-x-4'>
          {showSearch && (
            <div className='col-span-2'>
              <Search
                placeholder='Search'
                searchValue={searchQuery}
                onSearch={(value: string) => {
                  setSearchQuery(value);
                }}
                setTableInfo={setTableInfo}
                tableInfo={tableInfo}
                className='custom-search'
              />
            </div>
          )}
          <div>
            {showFilter && (
              <div className='flex col-span-2 gap-2 md:col-span-2 lg:col-span-2'>
                <Filter setOpen={setOpen} open={open} className='block'>
                  <Heading variant='body-xs' title={'Filter By:'} className={'filter-label'} />

                  <div className={`grid sm:grid-cols-${filterColumn} gap-x-5`}>
                    {data.filters?.map((filter, index) => (
                      <RenderComponent
                        key={`render-field-table-${index}`}
                        filter={filter}
                        filterControls={filterControls}
                        // tableInfo={tableInfo}
                        // setTableInfo={setTableInfo}
                        tableInfo={filterValues}
                        setFilterValue={handleFilterFieldChange}
                        setSelectedLabeledFilter={setSelectedLabeledFilter}
                      />
                    ))}
                  </div>
                  <div className='flex mt-3'>
                    <Button
                      title='Filter'
                      variant='primary'
                      onClick={() => {
                        setTableInfo({ ...filterValues, _force: Date.now() });
                        setLabeledFilter(selectedLabeledFilter || []);
                      }}
                    />
                  </div>
                </Filter>
                <div className='flex items-start col-span-5 lg:col-span-2 gap-x-4'>
                  <ResetFilterWrapper
                    onClick={() => {
                      setTableInfo({
                        ...InitialValuesForm.tableParams,
                        maxResultCount: totalCounts ?? 10,
                      });
                      setLabeledFilter([]);
                      setSelectedLabeledFilter([]);
                      setSearchQuery('');
                    }}>
                    <span className='material-symbols-outlined'>settings_backup_restore</span>
                    <Heading variant='body-s' title='Reset Filter' className='whitespace-nowrap' />
                  </ResetFilterWrapper>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className='flex col-span-2 gap-2 md:col-span-2 lg:col-span-2'>
          <div className={clsx('gap-x-4 w-full pl-4 flex justify-end')}>
            {components?.renderLeftComponent && components.renderLeftComponent()}
            {components?.renderRightToolbarButton && components.renderRightToolbarButton()}
          </div>
        </div>
      </div>
      {showSelectedFilter && (
        <SelectedLabelContainer>
          <>{console.log(labeledFilter)}</>
          {labeledFilter &&
            getFormattedKeyValuePairs(labeledFilter, {
              removeIdSuffix: true,
            }).map((item: any) => (
              <div className='selected-labels'>
                <Heading
                  variant='body-s'
                  title={`${item.key}: ${item.value}`}
                  color={COLORS.Silver}
                  className='font-semibold'
                />
              </div>
            ))}
        </SelectedLabelContainer>
      )}
    </>
  );
}

const RenderComponent = (props: {
  filter: DynamicTableFilter;
  filterControls?: Record<string, Record<string, any>>;
  tableInfo: TableParams;
  setFilterValue: (name: string, value: any) => void;
  setSelectedLabeledFilter?: any;
  // setTableInfo: (value: TableParams) => void;
}) => {
  // console.log("props in toolbar", props);

  const { filter, filterControls, tableInfo, setFilterValue, setSelectedLabeledFilter } = props;

  // const { tableInfo, setTableInfo } = useDynamicTable();
  const clears = filter.clears;
  const [dateValue, setDateValue] = useState<string | null>(null);
  useEffect(() => {
    setDateValue(filterControls?.[filter.name]?.value || tableInfo?.[filter.name] || null);
  }, [filterControls, filter.name, tableInfo]);

  // const handleDateChange = (value: string) => {
  //   setDateValue(value);
  //   const data = {
  //     ...tableInfo,
  //     [filter.name]: !isNull(value) ? moment(value).format(DATE_FORMAT) : "",
  //   };
  //   clearIfExist(data, clears);
  //   setTableInfo(data);
  // };
  // const handleDateChange = useCallback(
  //   debounce((value: string) => {
  //     const data = {
  //       ...tableInfo,
  //       [filter.name]: !isNull(value) ? moment(value).format(DATE_FORMAT) : '',
  //     };
  //     clearIfExist(data, clears);
  //     setTableInfo(data);
  //   }, 300),
  //   [tableInfo, filter.name, clears, setTableInfo]
  // );
  // const handleDateChange = (value: string) => {
  //   const data = {
  //     ...tableInfo,
  //     [filter.name]: !isNull(value) ? moment(value).format(DATE_FORMAT) : "",
  //   };
  //   clearIfExist(data, clears);
  //   setTableInfo(data);
  // };

  // const handleSelectChange = (value: boolean) => {
  //   const data = { ...tableInfo, [filter.name]: value };
  //   clearIfExist(data, clears);
  //   setTableInfo(data);
  // };

  // const handleMultiSelectChange = (values: any[]) => {
  //   // Handle special case for value 0 (if needed)
  //   if (values[0] === 0) values.shift();
  //   if (values.includes(0)) values = [0];
  //   if (isEmpty(values)) values = [];

  //   const data = { ...tableInfo, [filter.name]: values };
  //   clearIfExist(data, clears);
  //   setTableInfo(data);
  // };
  const handleDateChange = (value: string) => {
    setFilterValue(filter.name, !isNull(value) ? moment(value).format(DATE_FORMAT) : '');
    setSelectedLabeledFilter((prev = {}) => ({
      ...prev,
      [filter.name]: !isNull(value) ? moment(value).format(DATE_FORMAT) : '',
    }));
  };

  const handleSelectChange = (value: boolean) => {
    setFilterValue(filter.name, value);
    setSelectedLabeledFilter((prev = {}) => ({
      ...prev,
      [filter.name]: filter?.enum?.find((item) => item.value === value)?.label,
    }));
    // const labeledData = {
    //   ...labeledFilter,
    //   [filter.name]: filter?.enum?.find((item) => item.value === value)?.label,
    // };
    // setSelectedLabeledFilter(labeledData);
  };

  const handleMultiSelectChange = (values: any[]) => {
    if (values[0] === 0) values.shift();
    if (values.includes(0)) values = [0];
    if (isEmpty(values)) values = [];
    setFilterValue(filter.name, values);
    setSelectedLabeledFilter((prev = {}) => ({
      ...prev,
      [filter.name]: filter?.enum
        ?.filter((item) => values.includes(item.value))
        ?.map((item) => item.label),
    }));
    // const labeledData = {
    //   ...labeledFilter,
    //   [filter.name]: filter?.enum
    //     ?.filter((item) => values.includes(item.value))
    //     ?.map((item) => item.label),
    // };
    // setSelectedLabeledFilter(labeledData);
  };
  const renderDatePicker = () => {
    const value = filterControls?.[filter.name]?.value;
    const disabled = filterControls?.[filter.name]?.disabled;
    const dependency = filter?.dependencyName;
    const isDependencySet = dependency ? !!tableInfo[dependency]?.length : true; // Check if the dependency field in tableInfo has a value

    return (
      <CustomDatePicker
        name={filter.name}
        placeholder={filter.placeholder}
        onChange={handleDateChange}
        required
        // arrValue={value}
        arrValue={dateValue}
        // arrValue={value || tableInfo?.[filter.name]}
        min={filter.minDate}
        max={filter.maxDate}
        isFormik={false}
        disabled={disabled || !isDependencySet}
      />
    );
  };

  const renderSelect = () => {
    const value = filterControls?.[filter.name]?.value;
    const disabled = filterControls?.[filter.name]?.disabled;
    return (
      <FormSelect
        type='text'
        name={filter.name}
        placeholder={filter.placeholder}
        onChange={filter.isMulti ? handleMultiSelectChange : handleSelectChange}
        options={filter.enum}
        values={tableInfo[filter.name] ?? value}
        // values={value || filter.enum?.map((item) => item.value)}
        // values={filter.isMulti ? tableInfo[filter.name] || [] : value || tableInfo[filter.name]}
        arrValue={tableInfo[filter.name] ?? value}
        isFormik={false}
        disabled={disabled}
        multiple={filter.isMulti}
      />
    );
  };

  return (
    <>
      {filter.type === 'date' && renderDatePicker()}
      {filter.type === 'select' && renderSelect()}
      {filter.type === 'asyncSelect' && (
        <RenderAsyncSelect
          filter={filter}
          filterControls={filterControls}
          tableInfo={tableInfo}
          setFilterValue={setFilterValue}
          setSelectedLabeledFilter={setSelectedLabeledFilter}
        />
      )}
    </>
  );
};

const RenderAsyncSelect = (props: {
  filter: DynamicTableFilter;
  filterControls?: Record<string, Record<string, any>>;
  tableInfo: TableParams;
  setFilterValue: (name: string, value: any, clears?: string[]) => void;
  setSelectedLabeledFilter?: any;
}) => {
  const { filter, filterControls, tableInfo, setFilterValue, setSelectedLabeledFilter } = props;
  const { labeledFilter } = useDynamicTable();

  const { clears } = filter;
  const token = useSelector(selectToken);
  const path = filter.fetchFrom;
  // const { tableInfo, setTableInfo } = useDynamicTable();

  const value = filterControls?.[filter.name]?.value;
  const disabled = filterControls?.[filter.name]?.disabled;

  const optionsMapper = (
    data?: Record<string, any>[] | { items: Record<string, any>[]; totalCount: number }
  ) => {
    const { label, value } = filter.mapper || {};
    const itemsArray = Array.isArray(data) ? data : data?.items;

    // const options = itemsArray?.map((item) => ({
    //   label: item[label],
    //   value: item[value],
    // }));
    const options = itemsArray?.map((item) => {
      // If custom label formatting is needed
      if (filter.customLabelFormat) {
        const { baseField = label, startDateField, endDateField } = filter.customLabelFormat;

        const baseText = item[baseField] || '';
        const startDate = item[startDateField]
          ? moment(item[startDateField]).format(DATE_FORMAT)
          : 'N/A';
        const endDate = item[endDateField] ? moment(item[endDateField]).format(DATE_FORMAT) : 'N/A';

        return {
          label: `${baseText} (${startDate}-${endDate})`,
          value: item[value],
        };
      }

      // Default case
      return {
        label: item[label],
        value: item[value],
      };
    });

    if (filter.additionalOption) {
      const sortedOptions = options
        ?.concat({
          label: filter.additionalOption.label,
          value: filter.additionalOption.value,
        })
        .sort((a, b) => a.label.localeCompare(b.label));

      return sortedOptions;
    }

    return options;
  };

  const fetch = async () => {
    return (
      await axiosInstance.get(`${process.env.REACT_APP_WEALTHLANE_API_ENDPOINT}/${path}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    ).data;
  };

  // const handleSelectChange = (value: any, data: any) => {
  //   if (filter.storeLabelAs) {
  //     // Find the selected option to get its label
  //     const options = optionsMapper(data);
  //     const selectedOption = options?.find((option) => option.value === value);
  //     if (selectedOption) {
  //       const updatedData = {
  //         ...tableInfo,
  //         [filter.name]: value,
  //         [filter.storeLabelAs]: selectedOption.label,
  //       };
  //       clearIfExist(updatedData, clears);
  //       // setTableInfo(updatedData);
  //       setFilterValue(filter.name, value);
  //     }
  //   } else {
  //     const updatedData = { ...tableInfo, [filter.name]: value };
  //     clearIfExist(updatedData, clears);
  //     setTableInfo(updatedData);
  //   }
  // };

  // const handleMultiSelectChange = (values: any[], data: any) => {
  //   // Handle special case for value 0 (if needed)
  //   if (values[0] === 0) values.shift();
  //   if (values.includes(0)) values = [0];
  //   if (isEmpty(values)) values = [];

  //   if (filter.storeLabelAs && !isEmpty(values) && values[0] !== 0) {
  //     // Find the selected option to get its label
  //     const options = optionsMapper(data);
  //     const selectedLabels = values
  //       .map((val) => options?.find((option) => option.value === val)?.label)
  //       .filter(Boolean);
  //     if (selectedLabels) {
  //       const updatedData = {
  //         ...tableInfo,
  //         [filter.name]: values,
  //         [filter.storeLabelAs]: selectedLabels,
  //       };
  //       clearIfExist(updatedData, clears);
  //       setTableInfo(updatedData);
  //     }
  //   } else {
  //     const updatedData = { ...tableInfo, [filter.name]: values };
  //     clearIfExist(updatedData, clears);
  //     setTableInfo(updatedData);
  //   }
  // };
  const handleSelectChange = (value: any, data: any) => {
    if (filter.storeLabelAs) {
      // Find the selected option to get its label
      const options = optionsMapper(data);
      const selectedOption = options?.find((option) => option.value === value);
      if (selectedOption) {
        const updatedData = {
          ...tableInfo,
          [filter.name]: value,
          [filter.storeLabelAs]: selectedOption.label,
        };
        clearIfExist(updatedData, clears);
        // setTableInfo(updatedData);
        // setFilterValue(filter.name, value);
        setFilterValue(filter.name, value);
        setFilterValue(filter.storeLabelAs, selectedOption.label);
      }
    } else {
      const updatedData = { ...tableInfo, [filter.name]: value };
      clearIfExist(updatedData, clears);
      // setTableInfo(updatedData);
      setFilterValue(filter.name, value);
    }
    // const labeledData = {
    //   ...labeledFilter,
    //   [filter.name]: optionsMapper(data)?.find((item) => item.value === value)?.label,
    // };
    // setSelectedLabeledFilter(labeledData);
    setSelectedLabeledFilter((prev = {}) => ({
      ...prev,
      [filter.name]: optionsMapper(data)?.find((item) => item.value === value)?.label,
    }));
  };

  const handleMultiSelectChange = (values: any[], data: any) => {
    // Handle special case for value 0 (if needed)
    if (values[0] === 0) values.shift();
    if (values.includes(0)) values = [0];
    if (isEmpty(values)) values = [];
    const isArray = Array.isArray(values);
    if (filter.storeLabelAs && !isEmpty(values) && values[0] !== 0) {
      // Find the selected option to get its label
      const options = optionsMapper(data);
      const selectedLabels = values
        .map((val) => options?.find((option) => option.value === val)?.label)
        .filter(Boolean);
      if (selectedLabels) {
        const updatedData = {
          ...tableInfo,
          [filter.name]: values,
          [filter.storeLabelAs]: selectedLabels,
        };
        clearIfExist(updatedData, clears);
        // setTableInfo(updatedData);
        setFilterValue(filter.name, values);
        setFilterValue(filter.storeLabelAs, selectedLabels);
      }
    } else {
      const updatedData = { ...tableInfo, [filter.name]: values };
      clearIfExist(updatedData, clears);
      // setTableInfo(updatedData);
      setFilterValue(filter.name, values);
    }
    setSelectedLabeledFilter((prev = {}) => ({
      ...prev,
      [filter.name]:
        isArray &&
        values
          .map((item) => optionsMapper(data)?.find((option) => option.value === item)?.label)
          .filter(Boolean),
    }));
    // const labeledData = {
    //   ...labeledFilter,
    //   [filter.name]: isArray
    //     ? values.map((item) => optionsMapper(data)?.find((option) => option.value === item)?.label)
    //     : optionsMapper(data)?.find((item) => item.value === value)?.label,
    // };
    // setSelectedLabeledFilter(labeledData);
  };
  const { data, isLoading } = useQuery({
    queryKey: ['asyncSelectQuery', filter.name, path],
    queryFn: fetch,
    cacheTime: 1000 * 60 * 5,
  });

  // if (isLoading) return <SkeletonLine size='h1' />;

  return (
    <FormSelect
      type='text'
      name={filter.name}
      placeholder={filter.placeholder}
      onChange={(value: any) =>
        filter.isMulti ? handleMultiSelectChange(value, data) : handleSelectChange(value, data)
      }
      options={optionsMapper(data)}
      values={tableInfo[filter.name] ?? value}
      // values={optionsMapper(data)?.map((item) => item.value)}
      // values={
      //   filter.isMulti
      //     ? tableInfo[filter.name] || []
      //     : value || tableInfo[filter.name]
      // }
      arrValue={tableInfo[filter.name] ?? value}
      isFormik={false}
      isLoading={isLoading}
      inputType='Async'
      disabled={disabled}
      multiple={filter.isMulti}
    />
  );
};

// const RenderAsyncSelect = (props: {
//   filter: DynamicTableFilter;
//   filterControls?: Record<string, Record<string, any>>;
// }) => {
//   const { filter, filterControls } = props;
//   const { clears } = filter;
//   const token = useSelector(selectToken);
//   const path = filter.fetchFrom;
//   const { tableInfo, setTableInfo } = useDynamicTable();
//   console.log('Filter', filter);

//   const value = filterControls?.[filter.name]?.value;
//   const disabled = filterControls?.[filter.name]?.disabled;
//   console.log('value', value);

//   // const optionsMapper = (data?: Record<string, any>[]) => {
//   //   const { label, value } = filter.mapper || {};

//   //   return data?.map((item) => {
//   //     return {
//   //       label: item[label],
//   //       value: item[value],
//   //     };
//   //   });
//   // };
//   const optionsMapper = (
//     data?: Record<string, any>[] | { items: Record<string, any>[]; totalCount: number }
//   ) => {
//     const { label, value } = filter.mapper || {};
//     const itemsArray = Array.isArray(data) ? data : data?.items;

//     // const options = itemsArray?.map((item) => ({
//     //   label: item[label],
//     //   value: item[value],
//     // }));
//     const options = itemsArray?.map((item) => {
//       // If custom label formatting is needed
//       if (filter.customLabelFormat) {
//         const { baseField = label, startDateField, endDateField } = filter.customLabelFormat;

//         const baseText = item[baseField] || '';
//         const startDate = item[startDateField]
//           ? moment(item[startDateField]).format(DATE_FORMAT)
//           : 'N/A';
//         const endDate = item[endDateField] ? moment(item[endDateField]).format(DATE_FORMAT) : 'N/A';

//         return {
//           label: `${baseText} (${startDate}-${endDate})`,
//           value: item[value],
//         };
//       }

//       // Default case
//       return {
//         label: item[label],
//         value: item[value],
//       };
//     });

//     if (filter.additionalOption) {
//       const sortedOptions = options
//         ?.concat({
//           label: filter.additionalOption.label,
//           value: filter.additionalOption.value,
//         })
//         .sort((a, b) => a.label.localeCompare(b.label));

//       return sortedOptions;
//     }

//     return options;
//   };

//   const fetch = async () => {
//     return (
//       await axiosInstance.get(`${process.env.REACT_APP_WEALTHLANE_API_ENDPOINT}/${path}`, {
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       })
//     ).data;
//   };

//   const handleSelectChange = (value: boolean) => {
//     const data = { ...tableInfo, [filter.name]: value };
//     clearIfExist(data, clears);
//     setTableInfo(data);
//   };

//   const { data, isLoading } = useQuery({
//     queryKey: ['asyncSelectQuery', filter.name, path],
//     queryFn: fetch,
//     cacheTime: 1000 * 60 * 5,
//   });

//   // if (isLoading) return <SkeletonLine size='h1' />;

//   return (
//     <FormSelect
//       type='text'
//       name={filter.name}
//       placeholder={filter.placeholder}
//       onChange={handleSelectChange}
//       options={optionsMapper(data)}
//       // values={optionsMapper(data)?.map((item) => item.value) ?? value}
//       values={tableInfo[filter.name] ?? value}
//       arrValue={tableInfo[filter.name] ?? value}
//       isFormik={false}
//       isLoading={isLoading}
//       inputType='Async'
//       disabled={disabled}
//     />
//   );
// };

export default Toolbar;
